import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Mail, Send, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { usePageTitle } from '../components/PageTitle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  MapPin: <MapPin className="w-8 h-8" />,
  Phone: <Phone className="w-8 h-8" />,
  Mail: <Mail className="w-8 h-8" />,
  Clock: <Clock className="w-8 h-8" />
};

const Contact = () => {
  usePageTitle('Contact Us | Lehigh Human Support Services');
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  // Default content (fallback)
  const defaultContent = {
    hero_title: 'Get in Touch',
    hero_subtitle: "We're here to answer your questions and discuss how we can support you or your loved one",
    contact_info: [
      { icon: 'MapPin', title: 'Address', content: '5920 Hamilton Blvd, Suite 108\nAllentown, PA 18106' },
      { icon: 'Phone', title: 'Phone', content: '(484) 350-3601' },
      { icon: 'Mail', title: 'Email', content: 'info@lehigh-hss.com' },
      { icon: 'Clock', title: 'Office Hours', content: 'Monday - Friday\n9:00 AM - 6:00 PM\nClosed on Weekends' }
    ],
    form_title: 'Send Us a Message',
    form_subtitle: "Fill out the form below and we'll get back to you as soon as possible.",
    emergency_title: 'Emergency Support',
    emergency_text: 'For urgent matters outside of office hours, please call our 24/7 emergency line:',
    emergency_phone: '(484) 350-3601',
    emergency_note: '* For life-threatening emergencies, please call 911 immediately.',
    new_client_title: 'New Client Inquiries',
    new_client_text: "Interested in learning more about our services? We'd love to schedule a consultation to discuss how we can support you or your loved one. Please mention \"New Client Inquiry\" in your message, and we'll prioritize your request.",
    faq_title: 'Frequently Asked Questions',
    faq_subtitle: 'Quick answers to common questions',
    faqs: [
      { question: 'How do I get started with your services?', answer: "Contact us via phone, email, or the form above to schedule an initial consultation. We'll discuss your needs and determine the best support plan for you or your loved one." },
      { question: 'What areas do you serve?', answer: 'We primarily serve the Lehigh Valley area, including Allentown, Bethlehem, Easton, and surrounding communities in Pennsylvania.' },
      { question: 'Do you accept insurance or Medicaid?', answer: 'Yes, we work with various insurance providers and Medicaid programs. Contact us to discuss your specific coverage and payment options.' },
      { question: 'What qualifications do your staff have?', answer: 'All our Direct Support Professionals undergo comprehensive background checks, training, and certification. Many have years of experience in providing care for individuals with intellectual and developmental disabilities.' }
    ]
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/contact`);
      const merged = { ...defaultContent, ...response.data };
      setContent(merged);
    } catch (error) {
      console.error('Error fetching contact content:', error);
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/public/contact`, formData);
      toast({
        title: 'Message Sent!',
        description: 'Thank you for contacting us. We will respond within 24 hours.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const { contact_info, faqs } = content;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-white py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {content.hero_title}
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              {content.hero_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contact_info.map((info, index) => (
              <Card key={index} className="border-2 border-amber-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8 space-y-4 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500 rounded-full text-white">
                    {iconMap[info.icon] || <Phone className="w-8 h-8" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{info.title}</h3>
                  <p className="text-gray-600 whitespace-pre-line">{info.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="border-2 border-amber-200">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{content.form_title}</h2>
                <p className="text-gray-600 mb-8">{content.form_subtitle}</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-1"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-1"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="mt-1"
                        placeholder="How can we help?"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-1"
                      placeholder="Tell us more about your needs..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6 text-lg transition-all duration-300"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              <div className="rounded-2xl overflow-hidden shadow-2xl h-[400px]">
                <iframe
                  title="Lehigh HSS Location"
                  src={content.map_embed_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.8856449614726!2d-75.5638!3d40.6079!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDM2JzI4LjQiTiA3NcKwMzMnNDkuNyJX!5e0!3m2!1sen!2sus!4v1234567890"}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>

              <Card className="bg-gray-900 border-0">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-white">{content.emergency_title}</h3>
                  <p className="text-gray-300 leading-relaxed">{content.emergency_text}</p>
                  <div className="text-3xl font-bold text-amber-400">{content.emergency_phone}</div>
                  <p className="text-gray-400 text-sm">{content.emergency_note}</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-amber-200">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-2xl font-bold text-gray-900">{content.new_client_title}</h3>
                  <p className="text-gray-700 leading-relaxed">{content.new_client_text}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">{content.faq_title}</h2>
              <p className="text-xl text-gray-600">{content.faq_subtitle}</p>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 border-gray-200 hover:border-amber-300 transition-colors duration-300">
                  <CardContent className="p-8 space-y-3">
                    <h3 className="text-xl font-bold text-gray-900">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
